import * as React from 'react';
import clsx from 'clsx';

import {SetupGuideContext, Step, UserEventPropertiesMapContext} from 'common/onboarding/immersive-onboarding/types';

import {PropertyBreakdownPreviewCarouselV2} from '../chart-preview';
import {getBreakdownValues} from '../util';
import styles from './index.module.styl';
import {EventColumn} from './event-column';
import {PropertyColumn} from './property-column';
import {ConnectorWrapper} from './connector';

const BreakdownPreviewSectionCompleted = ({selectedEvent, selectedProperty, setSelectedEvent, setSelectedProperty}) => {
  const {eventKeys} = React.useContext(UserEventPropertiesMapContext);

  const event = !selectedEvent || !eventKeys.includes(selectedEvent) ? eventKeys[0] : selectedEvent;

  return (
    <div className={clsx(styles.breakdownContent, styles.narrow)}>
      <div className={clsx(styles.selectorContainer, styles.narrowSelectorContainer)}>
        <ConnectorWrapper>
          <EventColumn selectedEvent={event} setSelectedEvent={setSelectedEvent} skipAnimation />
          <PropertyColumn selectedEvent={event} setSelectedProperty={setSelectedProperty} skipAnimation />
        </ConnectorWrapper>
      </div>
      <div className={clsx(styles.preview, styles.narrowPreview)}>
        <PropertyBreakdownPreviewCarouselV2
          pauseRotation={!selectedProperty}
          events={eventKeys}
          selectedEvent={event}
          property={selectedProperty}
          breakdown={getBreakdownValues(selectedProperty)}
        />
      </div>
    </div>
  );
};

/* eslint-disable react/destructuring-assignment */
const BreakdownPreviewSection = ({
  showProperties,
  setShowProperties,
  selectedEvent,
  setSelectedProperty,
  setSelectedEvent,
  selectedProperty,
}) => {
  const {currentEventPropertiesMap, eventKeys} = React.useContext(UserEventPropertiesMapContext);

  React.useEffect(() => {
    let timerId;
    if (selectedEvent) {
      timerId = setTimeout(() => {
        setShowProperties(true);
      }, 500);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [selectedEvent]);

  // select first property when event is selected
  React.useEffect(() => {
    if (selectedEvent) setSelectedProperty(currentEventPropertiesMap[selectedEvent]?.[0] || null);
  }, [selectedEvent]);

  return (
    <div className={clsx(styles.breakdownContent, selectedEvent && styles.narrow)}>
      <div className={clsx(styles.selectorContainer, selectedEvent && styles.narrowSelectorContainer)}>
        <ConnectorWrapper>
          <EventColumn selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} />
          {selectedEvent && showProperties && (
            <PropertyColumn selectedEvent={selectedEvent} setSelectedProperty={setSelectedProperty} />
          )}
        </ConnectorWrapper>
      </div>
      <div className={clsx(styles.preview, !selectedProperty && styles.blur, selectedEvent && styles.narrowPreview)}>
        <PropertyBreakdownPreviewCarouselV2
          pauseRotation={!selectedProperty}
          events={eventKeys}
          selectedEvent={selectedEvent}
          property={selectedProperty}
          breakdown={getBreakdownValues(selectedProperty)}
        />
      </div>
    </div>
  );
};

export const EventSubsteps = ({showProperties, setShowProperties}) => {
  const {completed} = React.useContext(SetupGuideContext);
  const [selectedEvent, setSelectedEvent] = React.useState<string>(null);
  const [selectedProperty, setSelectedProperty] = React.useState<string>(null);

  if (completed[Step.Events]) {
    return (
      <BreakdownPreviewSectionCompleted
        selectedEvent={selectedEvent}
        setSelectedProperty={setSelectedProperty}
        setSelectedEvent={setSelectedEvent}
        selectedProperty={selectedProperty}
      />
    );
  }

  return (
    <BreakdownPreviewSection
      showProperties={showProperties}
      setShowProperties={setShowProperties}
      selectedEvent={selectedEvent}
      setSelectedProperty={setSelectedProperty}
      setSelectedEvent={setSelectedEvent}
      selectedProperty={selectedProperty}
    />
  );
};
